"use client";

import {
  Banner,
  BannerCollapseButton,
  ModalBody,
  Toast,
  ToastToggle,
} from "flowbite-react";
import Icon, { ICON_SIZES } from "@/components/library/Icon";
import { createContext, useContext, useEffect, useState } from "react";

import { BannerItem } from "@/utils/library/banner";
import Button from "@/components/library/Button";
import FancyLoader from "@/components/common/FancyLoader";
import { Modal } from "flowbite-react";
import React from "react";
import { SupportedLanguages } from "@/utils/library/language";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

type GenericModalProps = {
  open: boolean;
  message: string;
  description?: string;
};

type MainAlertProps = {
  status: "success" | "error" | "warning" | "info";
  message: string;
  secondsToClose?: 4 | 10 | 30;
  btnLabel?: string;
  btnAction?: () => void;
};

type AlertProps = {
  setAlert: (settings: MainAlertProps) => void;
  dismissAlert: () => void;
  showToast: boolean;
  toastSettings: MainAlertProps;
  isProcessing: GenericModalProps;
  setIsProcessing: (
    isSaving: boolean,
    message?: string,
    description?: string
  ) => void;
  bannerItems: BannerItem[];
  removeBannerItem: (id: string) => void;
};

const getIcon = (
  status: "success" | "error" | "warning" | "info"
):
  | "check-circle"
  | "circle-exclamation"
  | "triangle-exclamation"
  | "circle-info" => {
  switch (status) {
    case "success":
      return "check-circle";
    case "error":
      return "circle-exclamation";
    case "warning":
      return "triangle-exclamation";
    case "info":
      return "circle-info";
  }
};

export const AlertContext = createContext<AlertProps>({
  setAlert: () => {},
  dismissAlert: () => {},
  showToast: false,
  toastSettings: {
    status: "success",
    message: "",
  },
  isProcessing: {
    open: false,
    message: "",
  },
  setIsProcessing: () => {},
  bannerItems: [],
  removeBannerItem: () => {},
});

export const useAlert = () => useContext(AlertContext)!;

const useAlertFunction = (): AlertProps => {
  const { t: ct } = useTranslation("common");

  //Private State
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  //Public State
  const [showToast, setShowToast] = useState(false);
  const [toastSettings, setToastSettings] = useState<MainAlertProps>({
    status: "success",
    message: "",
  });
  const [isProcessing, setIsProcessing] = useState<GenericModalProps>({
    open: false,
    message: "",
    description: "",
  });
  const [bannerItems, setBannerItems] = useState<BannerItem[]>([]);

  const fetchBannerItems = async () => {
    const response = await fetch("/api/banner");

    if (!response.ok) {
      console.error("Failed to fetch banner items");
      return;
    }

    const dataPromise: Promise<BannerItem[]> = response.json();
    const ackedIdsString = window.localStorage.getItem("GAIL_banner_ids");
    const ackedIds = ackedIdsString ? ackedIdsString.split(",") : null;

    setBannerItems(
      (await dataPromise).filter((item) => {
        //Filter out any items already seen and acknowledged
        if (ackedIds && ackedIds.includes(item.id) && !item.isPersistent) {
          return false;
        }

        return true;
      })
    );
  };

  useEffect(() => {
    fetchBannerItems();

    //Check every 10 minutes for more Banner Items
    const interval = setInterval(
      () => {
        fetchBannerItems();
      },
      10 * 60 * 1000
    );

    return () => {
      clearInterval(interval);
    };
  }, []);

  //Public Methods
  const setAlert = (settings: MainAlertProps) => {
    setToastSettings(settings);
    setShowToast(true);

    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    const secondsToClose = settings.secondsToClose || 0;

    if (secondsToClose > 0) {
      const timeout = setTimeout(() => {
        setShowToast(false);
      }, secondsToClose * 1000);

      setTimeoutId(timeout);
    }
  };

  const dismissAlert = () => {
    setShowToast(false);
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
  };

  const handleRemoveBannerItem = (id: string) => {
    const ackedIds = window.localStorage.getItem("GAIL_banner_ids");
    if (ackedIds) {
      window.localStorage.setItem("GAIL_banner_ids", `${ackedIds},${id}`);
    } else {
      window.localStorage.setItem("GAIL_banner_ids", id);
    }

    setBannerItems(bannerItems.filter((item) => item.id !== id));
  };

  return {
    setAlert,
    dismissAlert,
    showToast,
    toastSettings,
    isProcessing,
    setIsProcessing: (isLoading, message, description) => {
      setIsProcessing({
        open: isLoading,
        message: message || ct("loadingDots"),
        description: description,
      });
    },
    bannerItems,
    removeBannerItem: handleRemoveBannerItem,
  };
};

export const AlertContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { i18n } = useTranslation();
  const alert = useAlertFunction();

  const {
    dismissAlert,
    showToast,
    toastSettings,
    isProcessing,
    bannerItems,
    removeBannerItem,
  } = alert;

  const icon = getIcon(toastSettings.status);

  return (
    <AlertContext.Provider value={alert}>
      {bannerItems.length > 0 ? (
        <Banner key={bannerItems[0].id}>
          <div
            className={clsx(
              "flex",
              "font-sans",
              "w-full",
              "justify-between",
              "p-4 md:py-2",
              "w-auto",
              "relative",
              "z-[9999]",
              {
                "bg-gail-primary dark:bg-gray-700 text-white":
                  bannerItems[0].status === "info",
                "bg-orange-50 dark:bg-yellow-900":
                  bannerItems[0].status === "warning",
                "bg-red-50 dark:bg-red-800": bannerItems[0].status === "error",
                "bg-green-50 dark:bg-green-900":
                  bannerItems[0].status === "success",
              }
            )}
          >
            <div className={clsx("md:mx-auto", "flex", "items-center")}>
              <div
                className={clsx(
                  "flex",
                  "items-center",
                  "text-sm",
                  "font-normal",
                  "text-800"
                )}
              >
                <div className="flex flex-col md:flex-row md:items-center">
                  <div>
                    <Icon icon={bannerItems[0].icon} className={clsx("mr-3")} />
                    {Object.keys(bannerItems[0].description).includes(
                      i18n.language as SupportedLanguages
                    )
                      ? bannerItems[0].description[
                          i18n.language as SupportedLanguages
                        ]
                      : bannerItems[0].description[SupportedLanguages.English]}
                  </div>
                  {(bannerItems[0].link ||
                    bannerItems[0].linkId ||
                    bannerItems[0].linkAction) &&
                    bannerItems[0].linkText && (
                      <Button
                        id={bannerItems[0].linkId || "bannerLink"}
                        href={bannerItems[0].link}
                        onClick={bannerItems[0].linkAction}
                        className={clsx(
                          "inline",
                          "font-medium",
                          "text-button_primary",
                          "!bg-transparent",
                          "underline",
                          "decoration-solid",
                          "underline-offset-2",
                          "cursor-pointer",
                          "hover:no-underline"
                        )}
                        textAlign="left"
                        target={
                          bannerItems[0].link?.startsWith("http")
                            ? "_blank"
                            : "_self"
                        }
                      >
                        {Object.keys(bannerItems[0].linkText).includes(
                          i18n.language as SupportedLanguages
                        )
                          ? bannerItems[0].linkText[
                              i18n.language as SupportedLanguages
                            ]
                          : bannerItems[0].linkText[SupportedLanguages.English]}
                      </Button>
                    )}
                </div>
              </div>
            </div>
            {!bannerItems[0].isPersistent && (
              <BannerCollapseButton
                color="orange"
                className={clsx("border-0", "bg-transparent", "text-800")}
                onClick={() => removeBannerItem(bannerItems[0].id)}
              >
                <Icon icon="close" />
              </BannerCollapseButton>
            )}
          </div>
        </Banner>
      ) : null}
      {children}
      <Toast
        className={clsx(
          "fixed",
          "top-24",
          "md:top-4",
          "right-4",
          "bg-gray-100",
          "max-w-sm",
          "border",
          "z-[100]",
          {
            "!top-[-100px]": !showToast,
            "border-green-500": toastSettings.status === "success",
            "border-red-500": toastSettings.status === "error",
            "border-yellow-500": toastSettings.status === "warning",
            "border-blue-500": toastSettings.status === "info",
          }
        )}
      >
        <span className={clsx({ hidden: toastSettings.status !== "success" })}>
          <Icon
            icon={icon}
            className={clsx("text-green-500")}
            size={ICON_SIZES.XL}
          />
        </span>
        <span className={clsx({ hidden: toastSettings.status !== "error" })}>
          <Icon
            icon={icon}
            className={clsx("text-red-500")}
            size={ICON_SIZES.XL}
          />
        </span>
        <span className={clsx({ hidden: toastSettings.status !== "warning" })}>
          <Icon
            icon={icon}
            className={clsx("text-yellow-500")}
            size={ICON_SIZES.XL}
          />
        </span>
        <span className={clsx({ hidden: toastSettings.status !== "info" })}>
          <Icon
            icon={icon}
            className={clsx("text-blue-500")}
            size={ICON_SIZES.XL}
          />
        </span>
        <div className="pl-4 pr-2 text-sm font-normal w-full grid gap-2">
          {toastSettings.message.split("\n").map((line: string, i: number) => (
            <div key={i}>{line}</div>
          ))}
        </div>
        {toastSettings.btnLabel && toastSettings.btnAction && (
          <Button
            className={clsx("mx-2", "float-right", {
              "text-green-500": toastSettings.status === "success",
              "text-red-500": toastSettings.status === "error",
              "text-yellow-500": toastSettings.status === "warning",
              "text-blue-500": toastSettings.status === "info",
            })}
            type="secondary"
            onClick={toastSettings.btnAction}
          >
            {toastSettings.btnLabel}
          </Button>
        )}
        <ToastToggle
          className={clsx("bg-gray-100")}
          onDismiss={() => dismissAlert()}
        />
      </Toast>
      <Modal show={isProcessing.open}>
        <ModalBody>
          <h1
            className={clsx("text-center", "text-xl", {
              "mb-4": !!isProcessing.description,
            })}
          >
            {isProcessing.message}
          </h1>
          {isProcessing.description && (
            <p className={clsx("text-center")}>{isProcessing.description}</p>
          )}
          <FancyLoader />
        </ModalBody>
      </Modal>
    </AlertContext.Provider>
  );
};
